"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_VAN_SERVICE = exports.defaultNavigationItems = void 0;
const react_1 = __importDefault(require("react"));
const set_1 = __importDefault(require("lodash/set"));
const config_firebase_1 = __importDefault(require("./config-firebase"));
const roles_1 = __importDefault(require("../roles"));
const icons_1 = require("@fielded/shared-ui/src/icons");
const tools_1 = require("@fielded/fs-api/lib/location/tools");
const SLTermsOfService_1 = __importDefault(require("./terms/SLTermsOfService"));
const SLCustomerAgreement_1 = __importDefault(require("./terms/SLCustomerAgreement"));
const SLLegalAndPrivacy_1 = __importDefault(require("./terms/SLLegalAndPrivacy"));
const DEFAULT_LOCATION_LEVELS = ['national', 'zone', 'state', 'lga', 'sdp'];
function getApplicationConfig(location) {
    // this function is likely to be called with the same location parameter many
    // times, so we memoize the result instead of recomputing every time
    getApplicationConfig[location] = getApplicationConfig[location] || (() => {
        const config = {
            ...commonConfig(),
            ...perTenantConfig(location),
            ...perTenantAndEnvConfig(location),
            firebase: (0, config_firebase_1.default)(location)
        };
        // This is just used to activate some things in dev and not staging/prod:
        Object.keys(config.environmentExtras || {}).forEach(key => {
            (0, set_1.default)(config, key, config.environmentExtras?.[key]);
        });
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_CONFIG_OVERRIDES) {
            let overrides;
            try {
                overrides = JSON.parse(process.env.REACT_APP_CONFIG_OVERRIDES);
            }
            catch (err) {
                console.warn('Error parsing config overrides in environment, skipping.');
            }
            Object.assign(config, overrides);
            config.performanceLogging = !!process.env.POUCHDB_PERF;
        }
        if (Array.isArray(config.databases)) {
            config.databases = config.databases.map((db) => {
                const withUpdates = {
                    ...db,
                    idEndpoint: (config.useDocDispenser ? config.cacheEndpoint : config.indicatorsEndpoint) + '/ids',
                    docEndpoint: config.cacheEndpoint + '/docs',
                    useDocDispenser: config.useDocDispenser,
                    useMultipleDispenserEndpoints: config.useMultipleDispenserEndpoints,
                    syncOnId: db.hasIdDispenser && !config.disableSyncOnId,
                    ...(db.idDispenserRoles && { idDispenserRequiredRoles: db.idDispenserRoles })
                };
                delete withUpdates.hasIdDispenser;
                delete withUpdates.idDispenserRoles;
                return withUpdates;
            });
        }
        return config;
    })();
    // This is temporary feature enabling users to test on staging with some of the features on
    return getApplicationConfig[location];
}
exports.default = getApplicationConfig;
exports.defaultNavigationItems = {
    home: {
        path: '/',
        title: 'Home',
        icon: react_1.default.createElement(icons_1.Home, null)
    },
    alerts: {
        path: '/alerts/',
        title: 'Alerts',
        icon: react_1.default.createElement(icons_1.ExclamationCircle, null)
    },
    analytics: {
        path: '/analytics/',
        title: 'Analytics',
        icon: react_1.default.createElement(icons_1.BarChart, null)
    },
    information: {
        path: '/information/',
        title: 'Information',
        icon: react_1.default.createElement(icons_1.InfoCircle, null)
    },
    reports: {
        path: '/reporting/',
        title: 'Stock reports',
        icon: react_1.default.createElement(icons_1.ListOl, null)
    },
    shipments: {
        path: '/shipments/',
        title: 'Shipments',
        icon: react_1.default.createElement(icons_1.Truck, null),
        // entityName is used in sentences that reference the main entity of this subapp,
        // for instance: 'Go to shipment'.
        // TODO: this might not be strictly tied to navigation, consider another place for this
        entityName: 'shipment'
    },
    deliveries: {
        path: '/shipments/',
        title: 'Deliveries',
        icon: react_1.default.createElement(icons_1.Truck, null),
    },
    settings: {
        path: '/settings/',
        title: 'Administration',
        icon: react_1.default.createElement(icons_1.Settings, null)
    },
    orders: {
        path: '/orders/',
        title: 'Orders',
        icon: react_1.default.createElement(icons_1.ListOl, null)
    }
};
const TENANT_PSM = 'psm';
const TENANT_SHELFLIFE = 'shelflife';
const TENANT_EPN = 'epn';
const TENANT_LASG = 'lasg';
const TENANT_KDSG = 'kdsg';
const TENANT_KNSG = 'knsg';
const TENANT_BNSG = 'bnsg';
const TENANT_GBSG = 'gbsg';
const TENANT_NAIROBI = 'nairobi';
const TENANT_RMS = 'rms';
const ENV_LOCAL = 'local';
const ENV_DEV = 'dev';
const ENV_DEV_LOCAL_DJANGO = 'dev-local-django';
const ENV_UAT = 'uat';
const ENV_STAGING = 'staging';
const ENV_DEMO = 'demo';
const ENV_PRODUCTION = 'production';
const ENV_PREVIEW = 'preview';
function commonConfig() {
    const origin = window.location.origin;
    return {
        dateFormats: {
            long: 'd LLL, yyyy',
            week: 'RRRR-[I]II'
        },
        enableLocalPersistence: false,
        disableSyncOnId: false,
        useDocDispenser: false,
        navigationItems: exports.defaultNavigationItems,
        destroyDataOnLogout: true,
        remoteDbUrl: `${origin}/db/`,
        indicatorsEndpoint: `${origin}/api`,
        cacheEndpoint: `${origin}/cache`,
        agaveApiUrl: `${origin}/agave/v1`,
        restApiUrl: `${origin}/avocado/`
    };
}
const HOST_MAP = new Map([
    // To deploy to a demo S3 bucket, add something like:
    // 'somefeature.demo.field.supply': [TENANT_VAN, ENV_DEV],
    //
    // To test the application on a device in your local network, start the
    // application setting `HOST=0.0.0.0 npm start` and and add your local network
    // IP here like:
    // ['192.168.178.25:3000', [TENANT_PSM, ENV_DEV]],
    //
    // PSM
    ['local.psm.localhost:3000', [TENANT_PSM, ENV_LOCAL]],
    ['localhost', [TENANT_PSM, ENV_LOCAL]],
    ['dev.psm.localhost:3000', [TENANT_PSM, ENV_DEV]],
    ['dev.nscip.field.supply', [TENANT_PSM, ENV_DEV]],
    ['sandbox.psm.field.supply', [TENANT_PSM, ENV_DEV]],
    ['staging.psm.localhost:3000', [TENANT_PSM, ENV_STAGING]],
    ['staging.nscip.field.supply', [TENANT_PSM, ENV_STAGING]],
    ['uat.nscip.field.supply', [TENANT_PSM, ENV_UAT]],
    ['nscip.field.supply', [TENANT_PSM, ENV_PRODUCTION]],
    ['healthlmis.ng', [TENANT_PSM, ENV_PRODUCTION]],
    // ['prod.psm.localhost:3000', [TENANT_PSM, ENV_PRODUCTION]],
    // SHELFFLIFE
    ['local.shelflife.localhost:3000', [TENANT_SHELFLIFE, ENV_LOCAL]],
    ['dev.shelflife.localhost:3000', [TENANT_SHELFLIFE, ENV_DEV]],
    ['dev-local-django.shelflife.localhost:3000', [TENANT_SHELFLIFE, ENV_DEV_LOCAL_DJANGO]],
    ['staging.shelflife.localhost:3000', [TENANT_SHELFLIFE, ENV_STAGING]],
    ['dev.shelflife.field.supply', [TENANT_SHELFLIFE, ENV_DEV]],
    ['staging.shelflife.field.supply', [TENANT_SHELFLIFE, ENV_STAGING]],
    ['uat.shelflife.field.supply', [TENANT_SHELFLIFE, ENV_UAT]],
    ['shelflife.field.supply', [TENANT_SHELFLIFE, ENV_PRODUCTION]],
    // ['prod.shelflife.localhost:3000', [TENANT_SHELFLIFE, ENV_PRODUCTION]],
    // EPN
    ['local.epn.localhost:3000', [TENANT_EPN, ENV_LOCAL]],
    ['dev.epn.localhost:3000', [TENANT_EPN, ENV_DEV]],
    ['dev.epn.field.supply', [TENANT_EPN, ENV_DEV]],
    ['staging.epn.localhost:3000', [TENANT_EPN, ENV_STAGING]],
    ['staging.epn.field.supply', [TENANT_EPN, ENV_STAGING]],
    ['uat.epn.field.supply', [TENANT_EPN, ENV_UAT]],
    ['epn.field.supply', [TENANT_EPN, ENV_PRODUCTION]],
    // LAGOS
    ['local.lagos.localhost:3000', [TENANT_LASG, ENV_LOCAL]],
    ['dev.lagos.localhost:3000', [TENANT_LASG, ENV_DEV]],
    ['dev.lagos.field.supply', [TENANT_LASG, ENV_DEV]],
    ['staging.lagos.localhost:3000', [TENANT_LASG, ENV_STAGING]],
    ['staging.lagos.field.supply', [TENANT_LASG, ENV_STAGING]],
    ['uat.lagos.field.supply', [TENANT_LASG, ENV_UAT]],
    ['lagos.field.supply', [TENANT_LASG, ENV_PRODUCTION]],
    // KADUNA
    ['local.kaduna.localhost:3000', [TENANT_KDSG, ENV_LOCAL]],
    ['dev.kaduna.localhost:3000', [TENANT_KDSG, ENV_DEV]],
    ['dev.kaduna.field.supply', [TENANT_KDSG, ENV_DEV]],
    ['staging.kaduna.localhost:3000', [TENANT_KDSG, ENV_STAGING]],
    ['staging.kaduna.field.supply', [TENANT_KDSG, ENV_STAGING]],
    ['uat.kaduna.field.supply', [TENANT_KDSG, ENV_UAT]],
    ['kaduna.field.supply', [TENANT_KDSG, ENV_PRODUCTION]],
    // KANO
    ['local.kano.localhost:3000', [TENANT_KNSG, ENV_LOCAL]],
    ['dev.kano.localhost:3000', [TENANT_KNSG, ENV_DEV]],
    ['dev.kano.field.supply', [TENANT_KNSG, ENV_DEV]],
    ['staging.kano.localhost:3000', [TENANT_KNSG, ENV_STAGING]],
    ['staging.kano.field.supply', [TENANT_KNSG, ENV_STAGING]],
    ['uat.kano.field.supply', [TENANT_KNSG, ENV_UAT]],
    ['kano.field.supply', [TENANT_KNSG, ENV_PRODUCTION]],
    // BORNO
    ['local.borno.localhost:3000', [TENANT_BNSG, ENV_LOCAL]],
    ['dev.borno.localhost:3000', [TENANT_BNSG, ENV_DEV]],
    ['dev.borno.field.supply', [TENANT_BNSG, ENV_DEV]],
    ['staging.borno.localhost:3000', [TENANT_BNSG, ENV_STAGING]],
    ['staging.borno.field.supply', [TENANT_BNSG, ENV_STAGING]],
    ['uat.borno.field.supply', [TENANT_BNSG, ENV_UAT]],
    ['borno.field.supply', [TENANT_BNSG, ENV_PRODUCTION]],
    // GOMBE
    ['local.gombe.localhost:3000', [TENANT_GBSG, ENV_LOCAL]],
    ['dev.gombe.localhost:3000', [TENANT_GBSG, ENV_DEV]],
    ['dev.gombe.field.supply', [TENANT_GBSG, ENV_DEV]],
    ['staging.gombe.localhost:3000', [TENANT_GBSG, ENV_STAGING]],
    ['staging.gombe.field.supply', [TENANT_GBSG, ENV_STAGING]],
    ['uat.gombe.field.supply', [TENANT_GBSG, ENV_UAT]],
    ['gombe.field.supply', [TENANT_GBSG, ENV_PRODUCTION]],
    // NAIROBI
    ['local.nairobi.localhost:3000', [TENANT_NAIROBI, ENV_LOCAL]],
    ['dev.nairobi.localhost:3000', [TENANT_NAIROBI, ENV_DEV]],
    ['dev.nairobi.field.supply', [TENANT_NAIROBI, ENV_DEV]],
    ['staging.nairobi.localhost:3000', [TENANT_NAIROBI, ENV_STAGING]],
    ['staging.nairobi.field.supply', [TENANT_NAIROBI, ENV_STAGING]],
    ['uat.nairobi.field.supply', [TENANT_NAIROBI, ENV_UAT]],
    ['nairobi.field.supply', [TENANT_NAIROBI, ENV_PRODUCTION]],
    // RMS
    ['local.rms.localhost:3000', [TENANT_RMS, ENV_LOCAL]],
    ['dev.rms.localhost:3000', [TENANT_RMS, ENV_DEV]],
    ['dev.rms.field.supply', [TENANT_RMS, ENV_DEV]],
    ['dev.sandbox.field.supply', [TENANT_RMS, ENV_DEV]],
    ['staging.rms.localhost:3000', [TENANT_RMS, ENV_STAGING]],
    ['staging.rms.field.supply', [TENANT_RMS, ENV_STAGING]],
    ['uat.rms.field.supply', [TENANT_RMS, ENV_UAT]],
    ['rms.field.supply', [TENANT_RMS, ENV_PRODUCTION]],
    ['rms.psm.field.supply', [TENANT_RMS, ENV_PRODUCTION]],
    // Demo/training
    ['demo.psm.field.supply', [TENANT_PSM, ENV_DEMO]],
    ['uat.psm.field.supply', [TENANT_PSM, ENV_UAT]],
    ['uat.psm.localhost:3000', [TENANT_PSM, ENV_UAT]],
    ['training.nhlmis.localhost:3000', [TENANT_PSM, ENV_DEMO]],
    ['training.nhlmis.field.supply', [TENANT_PSM, ENV_DEMO]],
    ['training.rms.field.supply', [TENANT_RMS, ENV_DEMO]],
    // Pull request previews
    [/^pr-\d+\.psm.preview.field.supply/, [TENANT_PSM, ENV_PREVIEW]],
    [/^pr-\d+\.epn.preview.field.supply/, [TENANT_EPN, ENV_PREVIEW]],
    [/^pr-\d+\.lagos.preview.field.supply/, [TENANT_LASG, ENV_PREVIEW]],
    [/^pr-\d+\.kaduna.preview.field.supply/, [TENANT_KDSG, ENV_PREVIEW]],
    [/^pr-\d+\.kano.preview.field.supply/, [TENANT_KNSG, ENV_PREVIEW]],
    [/^pr-\d+\.borno.preview.field.supply/, [TENANT_BNSG, ENV_PREVIEW]],
    [/^pr-\d+\.gombe.preview.field.supply/, [TENANT_GBSG, ENV_PREVIEW]],
    [/^pr-\d+\.nairobi.preview.field.supply/, [TENANT_NAIROBI, ENV_PREVIEW]],
    [/^pr-\d+\.shelflife.preview.field.supply/, [TENANT_SHELFLIFE, ENV_PREVIEW]]
]);
function parseHref(href) {
    const url = new window.URL(href);
    for (const [key, value] of HOST_MAP) {
        if (key instanceof RegExp) {
            if (key.test(url.host)) {
                return value;
            }
        }
        if (key === url.host) {
            return value;
        }
    }
    throw new Error(`Could not resolve config values for "${url.host}".`);
}
const localConfig = {
    'remoteDbUrl': 'http://localhost:9984/',
    'agaveApiUrl': 'http://localhost:3022/v1',
    disableSyncOnId: true,
    'environmentExtras': {
        'features.allowResetPass': true,
        'features.showEnvBanner': true
    }
};
const psmEnvConfig = {
    [ENV_LOCAL]: {
        ...localConfig,
        'environmentExtras': {
            ...localConfig.environmentExtras,
            'features.showCountSyncBanner': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.usePGShipments': true,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.stockUpdate': true,
            'features.product.prices': true,
            'features.shipments.showBatchInfo': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.usePGAnalytics': true,
            'features.useStickyReportingPeriod': true,
            'features.showGrossProfitIndicator': true,
            'features.showLossesIndicator': true,
            'features.settings.products.bulkImport': true
        }
    },
    [ENV_DEV]: {
        useDocDispenser: true,
        useMultipleDispenserEndpoints: true,
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.showEnvBanner': true,
            'features.showCountSyncBanner': true,
            'features.topUpOrdering': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.usePGShipments': true,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.stockUpdate': true,
            'features.product.prices': true,
            'features.shipments.showBatchInfo': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.usePGAnalytics': true,
            'features.useStickyReportingPeriod': true,
            'features.showGrossProfitIndicator': true,
            'features.showLossesIndicator': true,
            'features.settings.products.bulkImport': true
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '5167518dce600f8a8310e14cce6b8025'
        }
    },
    [ENV_PREVIEW]: {
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.showEnvBanner': true,
            'features.analyticsOFR': true,
            'features.showCountSyncBanner': true,
            'features.topUpOrdering': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.usePGShipments': true,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.stockUpdate': true,
            'features.shipments.showBatchInfo': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.usePGAnalytics': true,
            'features.useStickyReportingPeriod': true,
            'features.showGrossProfitIndicator': true,
            'features.showLossesIndicator': true,
            'features.settings.products.bulkImport': true
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '5167518dce600f8a8310e14cce6b8025'
        }
    },
    [ENV_STAGING]: {
        useDocDispenser: true,
        useMultipleDispenserEndpoints: true,
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.showEnvBanner': true,
            'features.showCountSyncBanner': true,
            'features.topUpOrdering': true,
            'features.usePGShipments': true,
            'features.routePlanning': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.autoPopulateReceived': true,
            'features.stockUpdate': true,
            'features.product.prices': true,
            'features.shipments.showBatchInfo': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.usePGAnalytics': true,
            'features.useStickyReportingPeriod': true,
            'features.showGrossProfitIndicator': true,
            'features.showLossesIndicator': true,
            'features.settings.products.bulkImport': true
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '5167518dce600f8a8310e14cce6b8025'
        }
    },
    [ENV_UAT]: {
        useDocDispenser: true,
        useMultipleDispenserEndpoints: true,
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.showCountSyncBanner': true,
            'features.topUpOrdering': true,
            'features.usePGShipments': true,
            'features.routePlanning': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showShipmentSyncBanner': true,
            'features.showFacilityView': true,
            'features.shipments.showBatchInfo': true,
            'features.autoPopulateReceived': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.product.prices': true,
            'features.longhaul': true,
            'features.showEnvBanner': true,
            'features.usePGAnalytics': true
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '7d4cb6bdfe25466db91d669169d6479b'
        }
    },
    [ENV_PRODUCTION]: {
        useDocDispenser: true,
        useMultipleDispenserEndpoints: true,
        'sentryDsn': 'https://b0b279c79013ef3afa1427d05db7fcd7@o145797.ingest.us.sentry.io/4508399381905408',
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.showCountSyncBanner': true,
            'features.topUpOrdering': true,
            'features.usePGShipments': true,
            'features.routePlanning': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showShipmentSyncBanner': true,
            'features.showFacilityView': true,
            'features.shipments.showBatchInfo': true,
            'features.autoPopulateReceived': true,
            'features.allowEditReceived': true,
            'features.useShipmentPrices': true,
            'features.product.prices': true,
            'features.longhaul': true,
            'features.usePGAnalytics': true
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '7d4cb6bdfe25466db91d669169d6479b'
        }
    },
    [ENV_DEMO]: {},
};
psmEnvConfig[ENV_DEMO] = { ...psmEnvConfig[ENV_STAGING] };
// #25703 (Longhaul v2) re-enabled on dev/staging only - see the
// features.orders.longhaulTransferV2 comment below for background.
Object.assign(psmEnvConfig, withFeatureFlag(psmEnvConfig, 'features.orders.longhaulTransferV2', [ENV_DEV, ENV_STAGING]));
const shelflifeEnvConfig = {
    [ENV_LOCAL]: {
        ...localConfig,
        // set this only for SL, PSM does not use Django
        'restApiUrl': 'http://localhost:8000/api/',
        'restTokenApiUrl': 'http://localhost:8000/api/',
        'agaveApiUrl': 'http://localhost:3022/v1',
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.shipments.allowFilterShipments': true,
            'feature.shipments.returnShipmentConfig': true,
            'features.shipments.showPackPointLedger': true,
            'features.shipments.verifyFPLocation': true,
            'features.payment.getPaymentMethod': true,
            'features.notifications.sendPlannerNotification': false,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.orders.bulkOrders': true,
            'features.shipments.showBatchInfo': true,
            'features.orders.onlineConfirmation': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true
        },
        paystack: {
            ke: {
                publicKey: 'pk_test_2980620a1c79aaab383931518a1e1292e008a9dd',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_test_3564fafcd9c5a8b49e8c93ba8e220a2583d461d9',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        }
    },
    [ENV_DEV]: {
        'restTokenApiUrl': 'https://dev.avocado.shelflife.field.supply/api/',
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.shipments.allowFilterShipments': true,
            'features.shipments.showPackPointLedger': true,
            'features.shipments.verifyFPLocation': true,
            'features.stockCount.adjustments': true,
            'features.stockCount.opensExistingReport': false,
            'features.showEnvBanner': true,
            'features.sendNewTopupEmailNotification': true,
            'feature.shipments.returnShipmentConfig': true,
            'features.notifications.testing': true,
            'features.payment.getPaymentMethod': true,
            'features.notifications.sendPlannerNotification': false,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.orders.bulkOrders': true,
            'features.shipments.showBatchInfo': true,
            'features.orders.onlineConfirmation': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true,
            'navigationItems.finances': {
                path: '/retailer/finances/',
                title: 'Finances',
                icon: react_1.default.createElement(icons_1.Landmark, null),
                entityName: 'finance'
            }
        },
        googleAnalytics: {
            g4Id: 'G-GKXEFY5CHX'
        },
        paystack: {
            ke: {
                publicKey: 'pk_test_2980620a1c79aaab383931518a1e1292e008a9dd',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_test_3564fafcd9c5a8b49e8c93ba8e220a2583d461d9',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '1a421453dac4c34c9d3ddca1f5736e5b'
        }
    },
    [ENV_PREVIEW]: {
        'restTokenApiUrl': 'https://dev.avocado.shelflife.field.supply/api/',
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.shipments.allowFilterShipments': true,
            'features.shipments.showPackPointLedger': true,
            'features.shipments.verifyFPLocation': true,
            'features.stockCount.adjustments': true,
            'features.stockCount.opensExistingReport': false,
            'features.showEnvBanner': true,
            'feature.shipments.returnShipmentConfig': true,
            'features.notifications.testing': true,
            'features.payment.getPaymentMethod': true,
            'features.notifications.sendPlannerNotification': false,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.orders.bulkOrders': true,
            'features.shipments.showBatchInfo': true,
            'features.orders.onlineConfirmation': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true,
            'navigationItems.finances': {
                path: '/retailer/finances/',
                title: 'Finances',
                icon: react_1.default.createElement(icons_1.Landmark, null),
                entityName: 'finance'
            }
        },
        googleAnalytics: {
            g4Id: 'G-GKXEFY5CHX'
        },
        paystack: {
            ke: {
                publicKey: 'pk_test_2980620a1c79aaab383931518a1e1292e008a9dd',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_test_3564fafcd9c5a8b49e8c93ba8e220a2583d461d9',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '1a421453dac4c34c9d3ddca1f5736e5b'
        }
    },
    [ENV_DEV_LOCAL_DJANGO]: {
        'remoteDbUrl': 'https://dev.shelflife.field.supply/db/',
        // see docs/Django-Web-Authentication, you'll have to make sure
        // your local_settings.py considers all requests authenticated.
        'restApiUrl': 'http://localhost:8000/api/',
        'restTokenApiUrl': 'https://dev.avocado.shelflife.field.supply/api/',
        'environmentExtras': {
            'navigationItems.finances': {
                path: '/retailer/finances/',
                title: 'Finances',
                icon: react_1.default.createElement(icons_1.Landmark, null),
                entityName: 'finance'
            },
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.stockCount.adjustments': true,
            'features.stockCount.opensExistingReport': false,
            'features.shipments.showBatchInfo': true,
            'features.useShipmentPrices': true
        }
    },
    [ENV_STAGING]: {
        'restTokenApiUrl': 'https://staging.avocado.shelflife.field.supply/api/',
        'environmentExtras': {
            'features.allowResetPass': true,
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.shipments.allowFilterShipments': true,
            'features.shipments.showPackPointLedger': true,
            'features.shipments.verifyFPLocation': true,
            'features.stockCount.adjustments': true,
            'features.stockCount.opensExistingReport': false,
            'feature.shipments.returnShipmentConfig': true,
            'features.showEnvBanner': true,
            'features.notifications.sendPlannerNotification': false,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.shipments.showBatchInfo': true,
            'features.orders.onlineConfirmation': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true,
            'navigationItems.finances': {
                path: '/retailer/finances/',
                title: 'Finances',
                icon: react_1.default.createElement(icons_1.Landmark, null),
                entityName: 'finance'
            }
        },
        googleAnalytics: {
            g4Id: 'G-4NCK33D5PF'
        },
        paystack: {
            ke: {
                publicKey: 'pk_test_2980620a1c79aaab383931518a1e1292e008a9dd',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_test_3564fafcd9c5a8b49e8c93ba8e220a2583d461d9',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: '1a421453dac4c34c9d3ddca1f5736e5b'
        }
    },
    [ENV_UAT]: {
        'restTokenApiUrl': 'https://uat.avocado.shelflife.field.supply/api/',
        'environmentExtras': {
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.stockCount.adjustments': true,
            'features.shipments.allowFilterShipments': true,
            'features.shipments.verifyFPLocation': true,
            'features.stockCount.opensExistingReport': false,
            'features.allowResetPass': true,
            'features.shipments.showPackPointLedger': true,
            'feature.shipments.returnShipmentConfig': true,
            'features.notifications.sendPlannerNotification': true,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.orders.onlineConfirmation': true,
            'features.shipments.showBatchInfo': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true,
            'features.showEnvBanner': true
        },
        googleAnalytics: {
            g4Id: 'G-GCKJ090L0C'
        },
        paystack: {
            ke: {
                publicKey: 'pk_live_19475a56a6731e12790bcdd77e355d25688f28b5',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_live_8870fa046b38241f32766d9ff198a8f460120c07',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: 'e1cdc327623e0c0e03b0ab05ad0ab501'
        }
    },
    [ENV_PRODUCTION]: {
        'restTokenApiUrl': 'https://avocado.shelflife.field.supply/api/',
        'sentryDsn': 'https://5299231cb42f4b7895ed836e60a23d2d@o145797.ingest.us.sentry.io/1189860',
        'environmentExtras': {
            'features.shipments.adjustments': true,
            'features.shipments.createNewShipment': true,
            'features.stockCount.adjustments': true,
            'features.shipments.allowFilterShipments': true,
            'features.stockCount.opensExistingReport': false,
            'features.allowResetPass': true,
            'features.shipments.showPackPointLedger': true,
            'feature.shipments.returnShipmentConfig': true,
            'features.notifications.sendPlannerNotification': true,
            'features.planning.centralResupply': true,
            'features.orders.pickPaymentPlanUpfront': true,
            'features.orders.onlineConfirmation': true,
            'features.shipments.showBatchInfo': true,
            'features.administration.onlinePriceList': true,
            'features.useShipmentPrices': true
        },
        googleAnalytics: {
            g4Id: 'G-GCKJ090L0C'
        },
        paystack: {
            ke: {
                publicKey: 'pk_live_19475a56a6731e12790bcdd77e355d25688f28b5',
                minAuthAmount: 300
            },
            ng: {
                publicKey: 'pk_live_8870fa046b38241f32766d9ff198a8f460120c07',
                minAuthAmount: 100
            }
        },
        mapbox: {
            mapboxAccessToken: 'pk.eyJ1IjoiZmllbGRtYWNoaW5lIiwiYSI6ImNseGFhNW54eDJ1b2kyanM5cnY4NDJyOHMifQ.nKNSPMzNblbM8gCF39Z38g'
        },
        amplitude: {
            apiKey: 'e1cdc327623e0c0e03b0ab05ad0ab501'
        }
    }
};
shelflifeEnvConfig[ENV_DEMO] = { ...shelflifeEnvConfig[ENV_STAGING] };
// #25197 (shipment confirmation permission) re-enabled everywhere - see the
// features.shipments.confirmDeliveryPermission comment below for background.
Object.assign(shelflifeEnvConfig, withFeatureFlag(shelflifeEnvConfig, 'features.shipments.confirmDeliveryPermission', [
    ENV_LOCAL,
    ENV_DEV,
    ENV_DEV_LOCAL_DJANGO,
    ENV_PREVIEW,
    ENV_STAGING,
    ENV_UAT,
    ENV_PRODUCTION,
    ENV_DEMO
]));
const psmResellerEnvConfig = {
    ...psmEnvConfig,
    [ENV_DEV]: {
        ...psmEnvConfig[ENV_DEV],
        'environmentExtras': {
            ...psmEnvConfig[ENV_DEV].environmentExtras,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.mosShipmentsFilter': true,
            'features.allowAdjustmentsAtPickup': true,
            'features.useShipmentPrices': true
        }
    },
    [ENV_STAGING]: {
        ...psmEnvConfig[ENV_STAGING],
        'environmentExtras': {
            ...psmEnvConfig[ENV_STAGING].environmentExtras,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.showEnvBanner': true,
            'features.mosShipmentsFilter': true,
            'features.useShipmentPrices': true
        }
    },
    [ENV_UAT]: {
        ...psmEnvConfig[ENV_UAT],
        'environmentExtras': {
            ...psmEnvConfig[ENV_UAT].environmentExtras,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.mosShipmentsFilter': true,
            'features.intercom': false,
            'features.useShipmentPrices': true
        }
    },
    [ENV_PRODUCTION]: {
        ...psmEnvConfig[ENV_PRODUCTION],
        'environmentExtras': {
            ...psmEnvConfig[ENV_PRODUCTION].environmentExtras,
            'features.autoPopulateReceived': true,
            'features.routePlanning': true,
            'features.longhaul': true,
            'features.lastMileDeliveries': true,
            'features.showDriverDeliveryView': true,
            'features.showFacilityView': true,
            'features.showShipmentSyncBanner': true,
            'features.mosShipmentsFilter': true,
            'features.intercom': false,
            'features.useShipmentPrices': true
        }
    }
};
psmResellerEnvConfig[ENV_DEMO] = { ...psmResellerEnvConfig[ENV_STAGING] };
// RMS specific environment config
const rmsEnvConfig = {
    ...psmResellerEnvConfig,
    [ENV_PRODUCTION]: {
        ...psmResellerEnvConfig[ENV_PRODUCTION],
        'sentryDsn': 'https://1f5d617c3b1423bba1d97275e187dc43@o145797.ingest.us.sentry.io/4511138107490304'
    }
};
// Nairobi-specific environment config
const nairobiEnvConfig = {
    ...psmResellerEnvConfig,
    [ENV_LOCAL]: {
        ...psmResellerEnvConfig[ENV_LOCAL],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_LOCAL]?.environmentExtras,
            'features.routePlanning': false,
            'features.stockReportingSave': true
        }
    },
    [ENV_DEV]: {
        ...psmResellerEnvConfig[ENV_DEV],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_DEV].environmentExtras,
            'features.routePlanning': false,
            'features.stockReportingSave': true
        }
    },
    [ENV_STAGING]: {
        ...psmResellerEnvConfig[ENV_STAGING],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_STAGING].environmentExtras,
            'features.routePlanning': false,
            'features.stockReportingSave': true
        }
    },
    [ENV_UAT]: {
        ...psmResellerEnvConfig[ENV_UAT],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_UAT].environmentExtras,
            'features.routePlanning': false,
            'features.stockReportingSave': true
        }
    },
    [ENV_PRODUCTION]: {
        ...psmResellerEnvConfig[ENV_PRODUCTION],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_PRODUCTION].environmentExtras,
            'features.routePlanning': false,
            'features.stockReportingSave': true
        }
    }
};
nairobiEnvConfig[ENV_DEMO] = { ...nairobiEnvConfig[ENV_STAGING] };
// Kano-specific environment config
const knsgEnvConfig = {
    ...psmResellerEnvConfig,
    [ENV_LOCAL]: {
        ...psmResellerEnvConfig[ENV_LOCAL],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_LOCAL]?.environmentExtras,
            'features.createBatchesAndStock': true
        }
    },
    [ENV_DEV]: {
        ...psmResellerEnvConfig[ENV_DEV],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_DEV].environmentExtras,
            'features.createBatchesAndStock': true
        }
    },
    [ENV_STAGING]: {
        ...psmResellerEnvConfig[ENV_STAGING],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_STAGING].environmentExtras,
            'features.createBatchesAndStock': true
        }
    },
    [ENV_UAT]: {
        ...psmResellerEnvConfig[ENV_UAT],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_UAT].environmentExtras,
            'features.createBatchesAndStock': true
        }
    },
    [ENV_PRODUCTION]: {
        ...psmResellerEnvConfig[ENV_PRODUCTION],
        'environmentExtras': {
            ...psmResellerEnvConfig[ENV_PRODUCTION].environmentExtras,
            'features.createBatchesAndStock': false
        }
    }
};
// This map contains configuration values that are unique for each tenant
// and environment combo
const psmResellerTenants = [
    TENANT_EPN,
    TENANT_LASG,
    TENANT_KDSG,
    TENANT_KNSG,
    TENANT_BNSG,
    TENANT_GBSG,
    TENANT_NAIROBI,
    TENANT_RMS
];
// Financial analytics is only enabled for these tenants
const financialAnalyticsTenants = [TENANT_EPN, TENANT_RMS];
// Self-ordering (facility ordering) is only enabled for these tenants
// and only in local, dev, and staging environments
const selfOrderingTenants = [TENANT_EPN, TENANT_KDSG, TENANT_KNSG];
// Warehouse review by group is only enabled for these tenants
// and only in local, dev, and staging environments
const warehouseReviewByGroupTenants = [TENANT_KNSG, TENANT_EPN];
// Payer/order-tag selection on facility ordering to attribute orders to a funding source (BHCP etc).
const payerOrgSelectionTenants = [TENANT_KDSG, TENANT_KNSG];
// Self Managed tenants: facility ordering does not go through our own ordering
// surface, it arrives from an external system (RMS SAP, the EPN Client App).
const selfManagedTenants = [TENANT_RMS, TENANT_EPN, TENANT_KNSG];
function withFeatureFlag(envConfig, featureKey, targetEnvs) {
    return targetEnvs.reduce((acc, env) => {
        acc[env] = {
            ...envConfig[env],
            environmentExtras: {
                ...envConfig[env]?.environmentExtras,
                [featureKey]: true
            }
        };
        return acc;
    }, { ...envConfig });
}
// Release kill switch for #25703 (Longhaul v2 FEFO batch assignment on supply
// plan approval), merged to develop with conflicts that made a clean revert
// impractical. Runtime behaviour is gated behind this key.
// - features.orders.longhaulTransferV2: gates the acceptance path used by
//   approveSupplyPlan (api/lib/order/index.js). Off = pre-#25703 behaviour,
//   plain status flip via setSupplyPlanTransfersStatus, no FEFO batch assignment.
//   Re-enabled on dev/staging only (see the withFeatureFlag call after
//   psmEnvConfig above) while production validation is pending.
// Release kill switch for #25197 (shipment confirmation permission), merged to
// develop with conflicts that made a clean revert impractical. Runtime behaviour
// is gated behind this key.
// - features.shipments.confirmDeliveryPermission: gates getCanConfirmDelivery
//   (web/src/subapps/shipments/common/utils.js) and the visibility of the
//   feature:shipment:confirm/feature:shipment:pack-immediate-purchase roles in
//   Settings > Users (UserAddEditContainerSL.js). Off = pre-#25118 behaviour, no
//   restriction on who can tick/edit/confirm a shipment. Re-enabled on every SL
//   environment (see the withFeatureFlag call after shelflifeEnvConfig above).
const ADDITIONAL_FEATURE_FLAGS = [
    {
        tenants: financialAnalyticsTenants,
        key: 'features.analytics.financialAnalytics',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO, ENV_UAT, ENV_PRODUCTION]
    },
    {
        tenants: selfOrderingTenants,
        key: 'features.orders.selfOrdering',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO]
    },
    {
        tenants: warehouseReviewByGroupTenants,
        key: 'features.orders.warehouseReviewByGroup',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO]
    },
    {
        tenants: payerOrgSelectionTenants,
        key: 'features.orders.payerOrgSelection',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO]
    },
    {
        tenants: selfManagedTenants,
        key: 'features.shipments.selfPickupShowPackedDate',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO]
    },
    {
        tenants: selfManagedTenants,
        key: 'features.shipments.driverShowRoutedDate',
        envs: [ENV_LOCAL, ENV_DEV, ENV_STAGING, ENV_DEMO]
    }
];
const PER_TENANT_RESELLER_ENV_CONFIG = {
    [TENANT_RMS]: rmsEnvConfig,
    [TENANT_KNSG]: knsgEnvConfig,
    [TENANT_NAIROBI]: nairobiEnvConfig
};
const PER_TENANT_AND_ENV = {
    [TENANT_PSM]: psmEnvConfig,
    ...Object.fromEntries(psmResellerTenants.map(tenant => {
        let envConfig = PER_TENANT_RESELLER_ENV_CONFIG[tenant] || psmResellerEnvConfig;
        ADDITIONAL_FEATURE_FLAGS.forEach(({ tenants, key, envs }) => {
            if (tenants.includes(tenant)) {
                envConfig = withFeatureFlag(envConfig, key, envs);
            }
        });
        return [tenant, envConfig];
    })),
    [TENANT_SHELFLIFE]: shelflifeEnvConfig
};
function perTenantAndEnvConfig(location) {
    const [tenant, env] = parseHref(location);
    return {
        ...PER_TENANT_AND_ENV[tenant][env],
        env
    };
}
const getRoles = (roles, selectedRoles = []) => {
    if (selectedRoles.length) {
        return selectedRoles.map(role => roles[role]);
    }
    return Object.keys(roles).reduce((allRoles, role) => ([
        ...allRoles,
        roles[role]
    ]), []);
};
const longInterval = 1000 * 60 * 4; // four minutes
const extraLongInterval = 1000 * 60 * 10; // ten minutes
/**
 * Configure roles for tenants.
 *
 * Roles are defined at the 'sub-app' level.
 *
 * Each 'sub-app' should define an array of available
 * roles for the given app as the default export from a `roles.js` file located
 * in the root of the subapp (and respective `build-lib/` directory).
 *
 * We then take care of importing roles from the various subapps in the
 * `role.js` file found in this repo to construct different role-sets for each
 * vertical.
 */
const allowedPSMSettingsRoles = ['admin', 'facilities', 'facilityApprovals', 'facilityChanges', 'facilityReadonly', 'products', 'productsReadonly',
    'users', 'funders', 'routes', 'createPrograms', 'onlineOffline', 'ordersRequester', 'ordersWarehouse', 'ordersBulk', 'ordersReset', 'historicDataEntry', 'forecasting', 'planning', 'approveLonghaul', 'uploadShipments', 'routePlanning', 'routeReviewing', 'carrierManagement'];
const psmRoles = [
    ...getRoles(roles_1.default.settings, allowedPSMSettingsRoles),
    ...getRoles(roles_1.default.qcAlerts),
    {
        ...roles_1.default.stockReporting.stockReporting
    },
    /**
     * the reason we're not using any stock-count prefix is
     * to avoid disabling reporting for anyone with that base role
     */
    {
        role: 'feature:read-only-reports',
        displayName: 'Reports: ReadOnly',
        description: 'User can only see the confirmation summary page and not the products and reports page after clicking on location'
    },
    {
        ...roles_1.default.shipments.shipments,
        description: 'User can create and edit shipments'
    },
    {
        ...roles_1.default.shipments.globalShipments
    },
    {
        ...roles_1.default.shipments.packShipments
    },
    {
        ...roles_1.default.shipments.lastMileDeliveries
    },
    {
        ...roles_1.default.analytics.operationsAnalyticsPG
    },
    {
        ...roles_1.default.analytics.financialAnalytics
    }
];
const shelflifeRoles = [
    {
        ...roles_1.default.stockReporting.stockReporting,
        displayName: 'Counts'
    },
    {
        ...roles_1.default.stockReporting.stockReportingAddProduct
    },
    {
        ...roles_1.default.shipments.shipments,
        displayName: 'Deliveries'
    },
    {
        ...roles_1.default.shipments.shipmentConfirm
    },
    {
        ...roles_1.default.shipments.shipmentPackImmediatePurchase
    },
    {
        ...roles_1.default.settings.admin,
        displayName: 'Admin'
    },
    {
        ...roles_1.default.settings.productsReadonly
    },
    {
        role: 'feature:read-only-reports',
        displayName: 'Counts: ReadOnly',
        description: 'User can only see the confirmation summary page and not the products and reports page after clicking on location'
    },
    {
        role: 'feature:settings:facilities:offline-edits',
        displayName: 'Offline Location Editing',
        description: 'User can propose and edit locations while offline'
    },
    {
        role: 'feature:settings:planning-calculator',
        displayName: 'Planning Calculator',
        description: 'User can trigger and also see status of planning calculator'
    },
    {
        ...roles_1.default.settings.superProductAdmin
    }
];
const psmTenantConfig = {
    name: 'Nigeria Health Logistics Management Information System',
    shortName: 'NHLMIS',
    program: 'Managed by Federal Ministry of Health',
    title: 'NHLMIS',
    unit: 'Federal Republic of Nigeria',
    logoUrl: `${window.location.origin}/psm/images/coat-of-arms-nigeria.png`,
    theme: 'psm',
    app: 'psm',
    appBase: 'psm',
    reportingPeriod: 'bimonthly',
    perCountryLocationIdHierarchy: {
        ng: tools_1.DEFAULT_LOCATION_ID_HIERARCHY
    },
    supportCountryInId: {
        ng: false,
    },
    forecastSource: 'allocation',
    features: {
        analytics: {
            nonAnalyticsRoles: [
                'feature:userRole:psm-facility',
                'feature:userRole:psm-lga',
                'feature:orders:warehouse',
                'feature:userRole:psm-carrier',
                'feature:userRole:psm-driver'
            ],
            showAnalyticsElements: {
                analyticsExportsButtons: true,
                viewByFunders: true,
                stockOutRateFiltersSection: true,
                monthsOfStockFiltersSection: true,
                locationFilter: true,
                tracerFilter: true,
                mosOptionsFilter: true,
                detailedMosSummaryCard: false,
                reduceSorCircleRadius: false,
                detailedStockCountSummaryCard: false
            },
            financialAnalytics: false,
            pgComparisonIndicators: ['stock-report-rate'],
            aggregationLevel: 'sdp',
            aggregationTier: 'sub-tiers',
            tracerProducts: true,
            expiryDefaultValue: 'values',
            // NOTE: `range` is for display only and must be kept in sync with `maxValue` and `maxUnit` manually.
            // Deriving `range` dynamically is not feasible due to inconsistent formatting across tenants (e.g. '11+ months' vs '6+ Months').
            expiryThresholds: [
                { size: 's', color: 'alert', range: '< 6 months', label: 'Written off', filterKey: 'warehouse', maxValue: 6, maxUnit: 'months' },
                { size: 's', color: 'warning', range: '< 10 Months', label: 'Almost written off', filterKey: 'warehouse', maxValue: 10, maxUnit: 'months' },
                { size: 's', color: 'ok', range: '11+ months', label: 'OK', filterKey: 'warehouse', maxValue: Infinity, maxUnit: 'months' },
                { size: 's', color: 'alert', range: '< 3 months', label: 'Written off', filterKey: 'sdp', maxValue: 3, maxUnit: 'months' },
                { size: 's', color: 'warning', range: '< 7 Months', label: 'Almost written off', filterKey: 'sdp', maxValue: 7, maxUnit: 'months' },
                { size: 's', color: 'ok', range: '8+ months', label: 'OK', filterKey: 'sdp', maxValue: Infinity, maxUnit: 'months' }
            ],
            stockOutRateThresholds: [
                { size: 's', color: 'ok', range: '0-5%', label: 'Good' },
                { size: 'm', color: 'warning', range: '5-10%', label: 'Alert' },
                { size: 'xl', color: 'alert', range: '>10%', label: 'Critical' }
            ]
        },
        analyticsOFR: true,
        forecasting: {
            enable: true,
            showNationalWarehouseOption: true,
            showForecastingElements: {
                createShipmentButton: true,
                showPlanningDate: true,
                showOffTrendInput: true,
                showShipments: true,
                showExportButton: false
            }
        },
        funders: true,
        routes: true,
        qcAlerts: true,
        locations: {
            levels: {
                ng: DEFAULT_LOCATION_LEVELS
            }
        },
        information: true,
        settings: {
            facilities: {
                approvals: true,
                bufferDays: 16,
                addOptions: {
                    version: '2.0.0'
                },
                exportImport: {
                    locations: true
                },
                editPhysicalAddress: true,
                editWard: true,
                selectGeoLocationLevel: 'lga',
                showGeoLocationChildren: true,
                // This is to automatically generate warehouse codes for new locations.
                // It is custom made for NHLMIS and it will need to be adapted if it
                // is required for other tenants.
                // Be careful to disable it when inheriting from this config.
                autoGenerateWarehouseCodes: true
            },
            funders: {
                edit: true,
                noun: 'funder',
                addOptions: {
                    version: '1.0.0'
                },
                showProgram: true
            },
            routes: {
                edit: true,
                noun: 'route',
                addOptions: {
                    version: '1.0.0'
                },
                showProgram: true
            },
            programs: true,
            users: true,
            products: {
                exportImport: {
                    options: {
                        exclude: ['productType', 'maxPrice', 'buyPrice', 'vat'],
                        additionalColumns: {
                            tracer: {
                                title: 'Tracer',
                                description: 'True if tracer, false otherwise. Required.'
                            },
                            genericParent: {
                                title: 'Generic Parent',
                                description: 'The Code of this product\'s generic parent. A generic product points at its own Code. Editable, required on create.'
                            },
                            genericFactor: {
                                title: 'Generic Factor',
                                description: 'Doses of the generic factor in this SKU. Equals SKU pack size / parent pack size. Must equal 1 for generic products. Editable, required on create.'
                            },
                            unitOfIssue: {
                                title: 'Unit of Issue',
                                description: 'Pack size or the number of basic units in the pack. Editable, required on create.'
                            },
                            unitOfReporting: {
                                title: 'Unit of Reporting',
                                description: 'The number of basic units in the pack size that stock reports capture. Editable, required on create.'
                            },
                            manufacturer: {
                                title: 'Manufacturer',
                                description: 'Manufacturer name -  only. Fill for manufacturer specific products. Optional, editable.'
                            },
                            alias: {
                                title: 'ONE Item Ids',
                                description: 'Other IDs this product is also known by. Optional, editable.',
                                transform: product => product.alias && product.alias.one
                                    ? product.alias.one.join(',')
                                    : ''
                            },
                            unitWeight: {
                                title: 'Unit Weight',
                                description: 'Weight of SKU in KGs (number only, no unit). Required when the manufacturer is not empty, editable.'
                            },
                            unitVolume: {
                                title: 'Unit Volume',
                                description: 'Volume of SKU in cubic meters (number only, no unit). Required when the manufacturer is not empty, editable.'
                            },
                            commodityType: {
                                title: 'Commodity Type',
                                description: 'Label for the product\'s type; lab, pharmaceutical etc. Optional, editable.'
                            }
                        }
                    }
                },
                editCode: true,
                editPrice: true,
                productType: false,
                editStorageType: true,
                productTypeOptions: [],
                storageTypeOptions: [
                    {
                        label: 'Non Cold Chain',
                        value: 'non-cold-chain'
                    },
                    {
                        label: 'Cold Chain',
                        value: 'cold-chain'
                    }
                ],
                basicUnitOptions: [
                    {
                        label: 'Doses',
                        value: 'doses'
                    },
                    {
                        label: 'Units',
                        value: 'units'
                    },
                    {
                        label: 'Vials',
                        value: 'vials'
                    },
                    {
                        label: 'Patient',
                        value: 'patient'
                    }
                ],
                showTracer: true,
                validateAlias: true,
                updateConfiguration: true
            },
            // PSM users re-route already-packed shipments via manual export, so include both statuses.
            warehouseShipmentExportStatuses: ['new', 'packed']
        },
        stockCount: {
            realtime: false,
            // This feature implies we need to check if there was a previous opening balance:
            editOpeningBalance: true,
            disableProductsNotInUse: true,
            reportDueDates: true,
            showLgaOnLocationListSearch: true,
            historicalDatePicker: true,
            useGenericParents: true,
            stickyReportingPeriod: false
        },
        intercom: false,
        stockReportExport: true,
        facilityExportLMD: true,
        shipments: {
            ...exports.defaultNavigationItems.shipments,
            title: 'Shipments',
            entityName: 'shipment',
            import: true,
            listProductQuantities: true,
            removeShipment: true,
            rescheduleShipment: true,
            listInGeoLocation: true,
            // This makes the shipment UI only use the list view
            // readOnlyShipments: true,
            editPSMShipment: true,
            // this puts the "Delete shipment" button on the list view
            removeShipmentInOverview: true,
            showDeliveryNote: true,
            // Skip OTP verification when driver delivers shipment (does not affect self-pickup OTP)
            askForDriverDeliveryOTP: true,
            verifyFPLocation: false,
            filterGenericProductsOnOutboundTemplate: false,
            /**
             * When true, EPOD confirmation checks whether to persist snapshots or log them.
             * PSM only; resellers and other tenants should keep this disabled.
            */
            checkPersistEpod: true,
            /**
             * When true, the self pickup list shows when a shipment was packed, labelled
             * "Packed on", instead of the unlabelled date its ordering system projected.
             * Turned on per tenant by ADDITIONAL_FEATURE_FLAGS (see selfManagedTenants)
            */
            selfPickupShowPackedDate: false,
            /**
             * When true, the driver app's deliveries list shows when the shipment's route
             * was created, labelled "Routed on", instead of the unlabelled date its
             * ordering system projected.
             * Turned on per tenant by ADDITIONAL_FEATURE_FLAGS (see selfManagedTenants)
            */
            driverShowRoutedDate: false
        },
        order: {
            appName: 'Orders',
            appEntityName: 'Order',
            facilityOrdering: false,
            noFunderSpecificLedger: false,
            pricingVisible: false
        }
    },
    databases: [
        {
            name: 'stock-count',
            bidirectional: true,
            hasIdDispenser: true,
            pollInterval: extraLongInterval,
            requiredRoles: [
                'feature:orders:warehouse',
                'feature:stock-reporting',
                'feature:read-only-reports'
            ]
        },
        {
            name: 'integrated-data',
            hasIdDispenser: true,
            pollInterval: extraLongInterval
        },
        {
            name: 'alerts',
            bidirectional: true,
            hasIdDispenser: true,
            pollInterval: extraLongInterval,
            requiredRole: 'feature:qc-alerts'
        },
        {
            name: 'master-data',
            bidirectional: true,
            hasIdDispenser: true,
            pollInterval: extraLongInterval,
            requiredRoles: [
                'feature:stock-reporting',
                'feature:orders:warehouse',
                'feature:orders:requester',
                'feature:global-shipments',
                'feature:userRole:psm-driver',
            ]
        },
        {
            name: 'van-shipments',
            bidirectional: true,
            hasIdDispenser: true,
            pollInterval: extraLongInterval,
            // if the user has any of these roles, we sync shipments
            requiredRoles: [
                'feature:orders:warehouse',
                'feature:shipments',
                'feature:stock-reporting',
                // This setup allows carriers to sync shipments.
                // Simply assigning the 'shipments' role to a user would grant access
                // to the Shipments navigation menu
                'feature:userRole:psm-carrier'
            ]
        }
    ],
    roles: [
        ...psmRoles
    ]
};
const shelflifeTenantConfig = {
    shortName: 'Shelf Life',
    name: 'Shelf Life',
    program: 'Powered by Field Supply',
    title: 'Field Supply',
    theme: 'shelflife',
    app: 'shelflife',
    appBase: 'shelflife',
    reportingPeriod: 'weekly',
    navigationItems: {
        ...exports.defaultNavigationItems,
        reports: {
            ...exports.defaultNavigationItems.reports,
            title: 'Counts'
        },
        shipments: {
            ...exports.defaultNavigationItems.shipments,
            title: 'Deliveries',
            entityName: 'delivery'
        }
    },
    forecastSource: 'smartForecast',
    features: {
        sync: {
            alwaysUseOnlineOffline: true
        },
        termsAndConditions: {
            version: '1.0.0',
            documents: [
                {
                    title: 'User Terms of Service',
                    shortTitle: 'User Terms',
                    description: 'the User Terms',
                    path: '/user-terms-and-conditions',
                    component: SLTermsOfService_1.default
                },
                {
                    title: 'Customer Agreement',
                    shortTitle: 'Customer Agreement',
                    description: 'Customer Agreement',
                    path: '/customer-agreement',
                    component: SLCustomerAgreement_1.default,
                    constraints: ['isRetailer']
                },
                {
                    title: 'Legal and Privacy',
                    shortTitle: 'Privacy and Policy',
                    description: 'the Privacy and Policy',
                    path: '/legal-and-privacy',
                    component: SLLegalAndPrivacy_1.default
                }
            ]
        },
        shipments: {
            addProduct: {
                showProductCode: true,
                syncWithAllocation: true // this only works for sent shipments and for directOrder allocation method for now
            },
            useCRSalesStats: true,
            removeShipment: true,
            showPlanningType: true,
            listInGeoLocation: true,
            routeSpecificUsersHideNewShipments: true,
            showDeliveryNote: true,
            showFunder: true,
            adjustments: false,
            createNewShipment: true,
            planning: true,
            useProductTranslation: true // used for SL products translation between markets
        },
        routes: true,
        locations: {
            levels: {
                ng: ['country', 'state', 'sdp', 'supplier'],
                ke: ['country', 'state', 'sdp', 'supplier']
            },
            includeRouteInLocationDoc: true,
            requiredLocationAdditionalFieldsForBulkImport: ["physicalAddress", "ward"]
        },
        showVAT: {
            ng: true,
            ke: true
        },
        settings: {
            shelflife: true,
            facilities: {
                approvals: false,
                // SL needs to enable facilities for the beginning of their reporting period.
                useLastDayOfPreviousPeriod: true,
                // creating new/update facility options
                addOptions: {
                    version: '2.0.0',
                    supportCountry: true
                },
                selectGeoLocationLevel: 'state',
                products: {
                    subscription: true,
                    manageSubscriptions: true
                },
                exportImport: {
                    allocation: {
                        createShipment: true
                    }
                },
                // This is to limit selecting services for SL locations
                // to be one per location, as SL services are currently 1:1 to a geolocation
                // (FCT, Lagos, Nasarawa, Kenya)
                // Having more than one service caused a bug in the stock report export
                oneServicePerLocation: true,
                // Migrating off google sheets for locations editing meant introducting 20+
                // fields to locations. Fields in this list are rendered with basic
                // UI elements (text input, number input), so anything with more custom
                // UI would come out of this list.
                additionalFields: true
            },
            routes: {
                edit: true,
                noun: 'route',
                addOptions: {
                    version: '1.0.0'
                },
                showOnUsersTable: true,
                filterByServiceLocation: true
            },
            users: {
                // this is to show the "create pharmacy users" button,
                // which leads to a different add/edit container for a pharamcy user
                // that has a different backend setup. SL only to start.
                createPharmacyUsers: true
            },
            planningCalculator: true,
            products: {
                editCode: true,
                editPrice: true,
                productType: true,
                editBuyPrice: true,
                editMaxPrice: true,
                showPrices: true,
                editVAT: true,
                // These regexes are re-used, do not use global flags
                // https://geekingfrog.com/blog/post/reuse-javascript-regexp-global-flag-gotcha
                validations: [
                    {
                        field: 'code',
                        regex: /^[0-9]+$/,
                        sample: '"12345" (numbers only)'
                    },
                    {
                        field: '_id',
                        regex: /^product:[0-9]+$/,
                        sample: 'product:12345'
                    }
                ],
                filterByName: true,
                productTypeOptions: [
                    {
                        label: 'Diluent',
                        value: 'diluent'
                    },
                    {
                        label: 'Dry',
                        value: 'dry'
                    },
                    {
                        label: 'Vaccine',
                        value: 'vaccine'
                    }
                ],
                basicUnitOptions: [
                    {
                        label: 'Doses',
                        value: 'doses'
                    },
                    {
                        label: 'Units',
                        value: 'units'
                    },
                    {
                        label: 'Vials',
                        value: 'vials'
                    }
                ]
            }
        },
        shelflife: true,
        stockCount: {
            downloadShipments: true,
            realtime: true,
            singleProductLine: true,
            showLocationContact: true,
            opensExistingReport: true,
            opensFutureReport: true,
            excludeProducts: 'configuration',
            adminOnlyHistory: true,
            reportDueDates: false,
            // show warning text when the opening balance is lower than the
            // closing balance
            dislayOpeningQuantityIssue: true,
            showStatementsDownload: true,
            adjustments: false,
            hideUnavailableProducts: true
        },
        order: {
            sl: true,
            appName: 'Planning',
            appEntityName: 'Plan',
            disableBulk: true
        },
        intercom: {
            appId: 'xqmandoq'
        },
        notifications: {
            enabled: true,
            retryInterval: 1
        }
    },
    programs: [
        'program:shelflife'
    ],
    databases: [
        {
            name: 'documents',
            requiredRole: 'feature:settings:facilities:offline-edits',
            bidirectionalRequiredRole: 'feature:settings:facilities:offline-edits',
            hasIdDispenser: true,
            pollInterval: extraLongInterval
        },
        {
            name: 'integrated-data',
            bidirectionalRequiredRole: 'feature:settings:facilities:offline-edits',
            hasIdDispenser: true,
            pollInterval: extraLongInterval
        },
        {
            name: 'van-shipments',
            bidirectional: true,
            requiredRole: 'feature:shipments',
            hasIdDispenser: true,
            pollInterval: longInterval
        },
        {
            name: 'stock-count',
            bidirectional: true,
            hasIdDispenser: true,
            pollInterval: extraLongInterval
        },
        {
            name: 'ledger-balances',
            hasIdDispenser: true,
            pollInterval: extraLongInterval
        }
        /*
         * Disabling because we were getting van doc on SL/Forth DB for the below DBs.
         * For this to work we need to deploy separate lambda functions for SL to avoid
         * cross domain weird syncing which is not safe (i can easily update our config.js
         * with another db link and they will start to get our data 🙃)
        {
          name: 'master-data',
          bidirectional: true
        },
        {
          name: 'alerts',
          bidirectional: false,
          pollInterval: alertsPollInterval
        }
        */
    ],
    roles: [
        ...shelflifeRoles
    ]
};
const psmResellerConfig = {
    ...psmTenantConfig,
    program: 'Powered by Field Supply',
    features: {
        ...psmTenantConfig.features,
        intercom: {
            appId: 'xqmandoq'
        },
        shipments: {
            ...psmTenantConfig.features.shipments,
            uploadShipments: true,
            filterGenericProductsOnOutboundTemplate: false,
            checkPersistEpod: false,
            facilitySelfPickup: true
        },
        locations: {
            ...psmTenantConfig.features.locations,
            requiredLocationAdditionalFieldsForBulkImport: []
        },
        settings: {
            ...psmTenantConfig.features.settings,
            stockUpdate: { enabled: true },
            users: {
                overviewCategory: 'internal'
            },
            facilities: {
                ...psmTenantConfig.features.settings.facilities,
                autoGenerateWarehouseCodes: false
            },
            // PSM users re-route already-packed shipments via manual export, so include both statuses.
            // excludeRoutedShipments is intentionally absent (defaults false) - PSM needs routed ones.
            warehouseShipmentExportStatuses: ['new', 'packed']
        },
        analytics: {
            ...psmTenantConfig.features.analytics,
            nonAnalyticsRoles: [
                'feature:userRole:psm-carrier',
                'feature:userRole:psm-driver'
            ],
            financialAnalytics: false,
            // only show these indicators in the analytics view
            indicators: ['stock-report-rate', 'stock-out-rate', 'months-of-stock', 'expiry-psm'],
            defaultView: 'products',
            defaultSelectedIndicator: 'months-of-stock',
            summaryAggregation: true
        },
        stockCount: {
            ...psmTenantConfig.features.stockCount,
            reportDueDates: false,
            editsPreviousReport: true
        },
        order: {
            ...psmTenantConfig.features.order,
            facilityOrdering: true,
            pricingVisible: true
        },
        information: false
    }
};
// this map contains configuration values that differ for each tenant, but
// stay the same for each deployment environment
// check the values in corresponding consts above above
const PER_TENANT = {
    [TENANT_PSM]: psmTenantConfig,
    [TENANT_SHELFLIFE]: shelflifeTenantConfig,
    [TENANT_EPN]: {
        ...psmResellerConfig,
        shortName: 'EPN',
        name: 'EPN',
        title: 'EPN',
        theme: 'epn',
        app: 'epn',
        logoUrl: `${window.location.origin}/epn/images/epn.png`,
        logoUrlWhite: `${window.location.origin}/epn/images/epn.png`,
        logoUrlDark: `${window.location.origin}/epn/images/epn-dark.png`,
        perCountryLocationIdHierarchy: {
            // NOTE: in dev location ids for KE are different, either just
            // ['country', 'state'] or ['country', 'zone', 'state', 'lga'].
            // We need to fix dev data to use the same kind of location ids
            // as in demo and production. Staging is a mix between
            // what is in dev and what is in demo, right now.
            ke: ['country', 'state', 'lga', 'sdp'],
            ng: tools_1.DEFAULT_LOCATION_ID_HIERARCHY
        },
        supportCountryInId: {
            ke: true,
            ng: false
        },
        features: {
            ...psmResellerConfig.features,
            analytics: {
                ...psmResellerConfig.features.analytics,
                financialAnalytics: false,
                showGrossProfitIndicator: true,
                showLossesIndicator: true
            },
            locations: {
                ...psmResellerConfig.features.locations,
                levels: {
                    // NOTE: in dev location levels for KE are different, either just
                    // ['national', 'state', 'sdp'] or
                    // ['national', 'zone', 'state', 'lga', 'sdp'].
                    // We need to fix dev data to use the same kind of location ids
                    // as in demo and production. Staging is a mix between
                    // what is in dev and what is in demo, right now.
                    ke: ['national', 'state', 'lga', 'sdp'],
                    ng: DEFAULT_LOCATION_LEVELS
                },
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                useGenericParents: true,
                stickyReportingPeriod: true
            },
            order: {
                ...psmResellerConfig.features.order,
                noFunderSpecificLedger: true
            }
        }
    },
    [TENANT_LASG]: {
        ...psmResellerConfig,
        shortName: 'Lagos',
        name: 'LAGOS',
        title: 'LAGOS',
        theme: 'lasg',
        app: 'lasg',
        logoUrl: `${window.location.origin}/lasg/images/lasg.png`,
        features: {
            ...psmResellerConfig.features,
            analytics: {
                ...psmResellerConfig.features.analytics,
                expiryCurrencyCode: 'NGN',
                expiryCurrencySymbol: '₦'
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                stickyReportingPeriod: true
            }
        }
    },
    [TENANT_KDSG]: {
        ...psmResellerConfig,
        shortName: 'Kaduna',
        name: 'KADUNA',
        title: 'KADUNA',
        theme: 'kdsg',
        app: 'kdsg',
        logoUrl: `${window.location.origin}/kdsg/images/kdsg.png`,
        features: {
            ...psmResellerConfig.features,
            analytics: {
                ...psmResellerConfig.features.analytics,
                expiryCurrencyCode: 'NGN',
                expiryCurrencySymbol: '₦'
            },
            order: {
                ...psmResellerConfig.features.order,
                noFunderSpecificLedger: true
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                stickyReportingPeriod: true
            }
        }
    },
    [TENANT_KNSG]: {
        ...psmResellerConfig,
        shortName: 'Kano',
        name: 'KANO',
        title: 'KANO',
        theme: 'knsg',
        app: 'knsg',
        logoUrl: `${window.location.origin}/knsg/images/knsg.png`,
        features: {
            ...psmResellerConfig.features,
            analytics: {
                ...psmResellerConfig.features.analytics,
                expiryCurrencyCode: 'NGN',
                expiryCurrencySymbol: '₦'
            },
            order: {
                ...psmResellerConfig.features.order,
                noFunderSpecificLedger: true
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                stickyReportingPeriod: true
            },
            shipments: {
                // Only listInGeoLocation is restored here — it's what drivers' shipment
                // queries depend on (see PR #25544, which accidentally dropped every
                // inherited shipments flag by overwriting this object instead of
                // spreading psmResellerConfig.features.shipments into it). The rest of
                // psmResellerConfig.features.shipments (e.g. askForDriverDeliveryOTP)
                // is deliberately left unset to avoid reintroducing unrelated behavior
                // changes for Kano alongside this fix.
                listInGeoLocation: true,
                facilitySelfPickup: false
            },
            settings: {
                ...psmResellerConfig.features.settings,
                stockUpdate: {
                    ...psmResellerConfig.features.settings.stockUpdate,
                    createBatchesAndStock: true
                },
                users: {
                    createCarrierUsers: true
                },
                facilities: {
                    ...psmResellerConfig.features.settings.facilities,
                    editSupplier: true
                },
                warehouseShipmentExportStatuses: ['new', 'packed'],
                routes: {
                    ...psmResellerConfig.features.settings.routes,
                    persistentRouting: true
                }
            },
            routes: {
                hideLga: true,
                defaultCarrierName: 'RMS Carrier',
                manualUploadUsesBulkImport: true,
                validateSubOrderId: false,
                hideAutomaticRouting: true,
                driverInfoVisibleToAll: true
            },
            psmResellerWarehouseUsersCanMoveShipmentsToPacked: true,
            psmResellerWarehouseUsersCanMoveUnroutedShipmentsToPacked: true
        }
    },
    [TENANT_BNSG]: {
        ...psmResellerConfig,
        shortName: 'Borno',
        name: 'BORNO',
        title: 'BORNO',
        theme: 'bnsg',
        app: 'bnsg',
        logoUrl: `${window.location.origin}/bnsg/images/bnsg.png`,
    },
    [TENANT_NAIROBI]: {
        ...psmResellerConfig,
        shortName: 'Nairobi',
        name: 'NAIROBI',
        title: 'NAIROBI',
        theme: 'nairobi',
        app: 'nairobi',
        logoUrl: `${window.location.origin}/nairobi/images/nairobi.png`,
        logoUrlDark: `${window.location.origin}/nairobi/images/nairobi_dark.png`,
        features: {
            ...psmResellerConfig.features,
            locations: {
                ...psmResellerConfig.features.locations,
                levels: {
                    // Nairobi uses the same location hierarchy as other Kenya programs eg ke-epn
                    // so we need to override the levels here
                    ke: ['national', 'state', 'lga', 'sdp'],
                },
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                stickyReportingPeriod: true
            }
        }
    },
    [TENANT_RMS]: {
        ...psmResellerConfig,
        shortName: 'RMS',
        name: 'RMS',
        title: 'RMS',
        theme: 'rms',
        app: 'rms',
        logoUrl: `${window.location.origin}/rms/images/rms.png`,
        logoUrlDark: `${window.location.origin}/rms/images/rms-dark.png`,
        perCountryLocationIdHierarchy: {
            rw: tools_1.DEFAULT_LOCATION_ID_HIERARCHY
        },
        supportCountryInId: {
            rw: true
        },
        features: {
            ...psmResellerConfig.features,
            routePlanningCanBeStateIgnorant: true,
            // Enable strict local mode for RMS drivers to avoid remote CouchDB fallbacks
            // that cause 3+ minute delays during peak hours (issue #21518)
            'shipments:strictLocalMode': true,
            stockTransfers: {
                showRealStatus: true
            },
            analytics: {
                ...psmResellerConfig.features.analytics,
                isCycleBased: false,
                customTermsSize: 3500,
                hasPerProductThresholds: true,
                customMosSummaryLevels: {
                    okMax: 10,
                    warningMax: 20
                },
                customTenantHeader: true,
                showAnalyticsElements: {
                    analyticsExportsButtons: false,
                    viewByFunders: false,
                    stockOutRateFiltersSection: false,
                    monthsOfStockFiltersSection: false,
                    locationFilter: false,
                    tracerFilter: false,
                    mosOptionsFilter: false,
                    detailedMosSummaryCard: true,
                    detailedStockCountSummaryCard: true,
                    locationSearchOnSOR: false,
                    reduceSorCircleRadius: true,
                    showMOSExcessiveUnitsColumn: true
                },
                indicators: ['stock-out-rate', 'months-of-stock', 'expiry-psm'],
                pgComparisonIndicators: [],
                expiryCurrencyCode: 'RWF',
                expiryCurrencySymbol: 'RWF',
                aggregationLevel: 'warehouse',
                aggregationTier: 'warehouses',
                tracerProducts: false,
                summaryAggregation: false,
                expiryDefaultValue: 'amounts',
                // NOTE: `range` is for display only and must be kept in sync with `maxValue` and `maxUnit` manually.
                // Deriving `range` dynamically is not feasible due to inconsistent formatting across tenants (e.g. '11+ months' vs '6+ Months').
                expiryThresholds: [
                    { size: 's', color: 'alert', range: '< 7 days', label: 'Written off', filterKey: 'warehouse', maxValue: 7, maxUnit: 'days' },
                    { size: 's', color: 'highwarning', range: '< 3 Months', label: 'At High Risk', filterKey: 'warehouse', maxValue: 3, maxUnit: 'months' },
                    { size: 's', color: 'warning', range: '< 6 Months', label: 'At Risk', filterKey: 'warehouse', maxValue: 6, maxUnit: 'months' },
                    { size: 's', color: 'ok', range: '6+ Months', label: 'OK', filterKey: 'warehouse', maxValue: Infinity, maxUnit: 'months' }
                ],
                stockOutRateThresholds: [
                    { size: 's', color: 'ok', range: '0-5%', label: 'Good' },
                    { size: 'm', color: 'warning', range: '5-20%', label: 'Alert' },
                    { size: 'xl', color: 'alert', range: '>20%', label: 'Critical' }
                ]
            },
            forecasting: {
                enable: true,
                showNationalWarehouseOption: true,
                showPONumberAndSupplier: true,
                showForecastingElements: {
                    createShipmentButton: false,
                    showPlanningDate: false,
                    showOffTrendInput: false,
                    showShipments: true,
                    showExportButton: true
                }
            },
            outboundShipments: {
                addRoutesToShipments: false
            },
            psmResellerWarehouseUsersCanMoveShipmentsToPacked: true,
            shipments: {
                ...psmResellerConfig.features.shipments,
                // Enable signature form for distribution shipments when marking "arrived" as "received"
                showSignatureForDistributionArrived: true,
                uploadShipments: false
            },
            locations: {
                ...psmResellerConfig.features.locations,
                levels: {
                    rw: DEFAULT_LOCATION_LEVELS
                },
            },
            settings: {
                ...psmResellerConfig.features.settings,
                users: {
                    createCarrierUsers: true
                },
                // RMS uses both 'new' and 'packed' statuses for warehouse shipment exports
                warehouseShipmentExportStatuses: ['new', 'packed'],
                routes: {
                    ...psmResellerConfig.features.settings.routes,
                    // Enable persistent routing - shows ungrouped shipments for users to pick and group into routes.
                    //
                    // NOTE: Route config is inconsistent across the codebase:
                    // - features.routes is typed as `boolean` (Config.ts:80), but RMS overrides it
                    //   as an object with UI flags (hideLga, driverInfoVisibleToAll, etc.) below
                    // - features.settings.routes is a typed object for route admin settings
                    //   (edit, showProgram, persistentRouting, etc.)
                    //
                    // The untyped features.routes object (see RMS config below at ~line 1608) should
                    // either be merged into features.settings.routes with proper typing, or
                    // FeaturesConfig.routes should be updated to support both boolean | RouteUIConfig.
                    persistentRouting: true,
                    // Filter drivers by carrier for warehouse users based on their warehouse's suppliesStateSDPs
                    filterDriversByCarrier: true
                }
            },
            routes: {
                hideLga: true,
                // Auto-assign this carrier by name on manual upload when uploader is planner+reviewer
                defaultCarrierName: 'RMS Carrier',
                manualUploadUsesBulkImport: true,
                validateSubOrderId: false,
                hideAutomaticRouting: true,
                driverInfoVisibleToAll: true,
                status: ['new', 'packed']
            },
            stockCount: {
                ...psmResellerConfig.features.stockCount,
                stockReportWeekly: true,
                hideStockReportsInNav: true
            }
        }
    },
    [TENANT_GBSG]: {
        ...psmResellerConfig,
        shortName: 'Gombe',
        name: 'GOMBE',
        title: 'GOMBE',
        theme: 'gbsg',
        app: 'gbsg',
        logoUrl: `${window.location.origin}/gbsg/images/gbsg.png`,
    }
};
function perTenantConfig(location) {
    const [tenant] = parseHref(location);
    return PER_TENANT[tenant] || {};
}
exports.DEFAULT_VAN_SERVICE = {
    id: 'program:immunization:service:immunization',
    program: {
        reportingPeriod: 'weekly',
        report: {
            periods: [{
                    periodType: {
                        unit: 'week',
                        quantity: 1
                    },
                    entryPeriodRule: 'current-period'
                }]
        }
    }
};
